import React, { forwardRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { formatInnerHTML } from 'utils/misc'
import { getCurrentUTCDateTime } from 'utils/time'
import SurveyQuestion from '../../SurveyQuestion'
import MakeScrollable from '../../MakeScrollable'
import MatrixRow from './MatrixRow'
import { nextOrSkip } from '../../utils'
import { NextButton } from '../../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../../utils/instrumentUtils'

const onCompleteMatrixQuestion = (props, responses, timeAnswered) => {
  const {
    completeQuestion,
    id,
    isConsent,
    isLastQuestion,
    isSibling,
    nextQ,
    nextQId,
    question,
    submissionSurvey,
    webSurvey,
  } = props
  const { label, type, attributes, questions_order, sibling_id, formula } = question
  const { required } = attributes
  const completed = responses.length === questions_order.length
  const submission = {
    id,
    label,
    name: label,
    type,
    required,
    state: completed ? 'ANSWERED' : 'SKIPPED',
    answer: responses,
    formula,
  }
  if (completed) submission.time_answered = timeAnswered
  if (sibling_id) submission.sibling_id = sibling_id
  const currentAnswer = { [id]: responses }
  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })
  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    false,
    shouldRenderNextQ,
  )
}

const Matrix = forwardRef((props, ref) => {
  const {
    className,
    question,
    id,
    isSibling,
    renderBackButton,
    strings,
    altQuestionFooter,
  } = props
  const [responses, setResponses] = useState([])
  const [timeAnswered, setTimeAnswered] = useState(null)

  useEffect(() => {
    if (isSibling) onCompleteMatrixQuestion(props, [])
  }, [])

  const _className = className ? ` ${className}` : ''
  const { label, questions, questions_order, attributes, comments } = question
  const { required } = attributes
  const completed = responses.length === questions_order.length

  const updateMatrixRowQuestion = (questionId, answer) => {
    const currentDateTime = getCurrentUTCDateTime()
    setTimeAnswered(currentDateTime)
    const currentReponseIdx = responses.findIndex(response => response.question === questionId)
    const answerObj = {
      question: questionId,
      label: questions[questionId].label,
      answer,
    }

    if (currentReponseIdx === -1) {
      const nextResponses = [...responses, answerObj]
      setResponses(nextResponses)
      if (isSibling) onCompleteMatrixQuestion(props, nextResponses, currentDateTime)
    } else {
      let responsesCopy = [...responses]
      if (answer.length === 0) {
        responsesCopy = responsesCopy.filter(response => response.question !== questionId)
      } else {
        responsesCopy[currentReponseIdx] = answerObj
      }
      setResponses(responsesCopy)
      if (isSibling) onCompleteMatrixQuestion(props, responsesCopy, currentDateTime)
    }
  }

  const content = (
    <div className={`survey-question matrix${_className}`}>
      <p className='question-label'>
        <span dangerouslySetInnerHTML={{ __html: formatInnerHTML(label) }} />
      </p>
      <MatrixRow isHeader question={question} id={id} />
      {questions_order.map((qId, idx) => {
        return (
          <MatrixRow
            {...props}
            id={id}
            isLastRow={idx === questions_order.length - 1}
            key={qId}
            questionId={qId}
            updateMatrixRowQuestion={updateMatrixRowQuestion}
          />
        )
      })}
      {altQuestionFooter}
      {comments && (
        <div className='comments'>
          <p className='instructions'>
            {strings.instructions_comments || 'Instructions/Comments'}
          </p>
          <span>{comments}</span>
        </div>
      )}
      {!isSibling && (
        <div className='button-list'>
          {renderBackButton(id)}
          <NextButton
            disabled={required && !completed}
            content={nextOrSkip(required, completed, strings)}
            completionFunc={() => {
              onCompleteMatrixQuestion(props, responses, timeAnswered)
            }}
          />
        </div>
      )}
    </div>
  )

  return isSibling ? (
    <div ref={ref}>{content}</div>
  ) : (
    <div ref={ref}>
      <SurveyQuestion id={id}>
        <MakeScrollable>{content}</MakeScrollable>
      </SurveyQuestion>
    </div>
  )
})

export default Matrix

Matrix.propTypes = {
  altQuestionFooter: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  isSibling: PropTypes.bool,
  siblingOrder: PropTypes.arrayOf(PropTypes.string),
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
}
